<template>
	<section class="roi-calculator">
		<div class="box-container">
			<!-- Box 1: User Inputs -->
			<div class="box">
				<div class="input-group" v-for="(input, index) in box1Inputs" :key="'box1-' + index">
					<label class="input-label">{{ $t(input) }}</label>
					<!-- Bind each input field using v-model -->
					<input v-model.number="inputValues[input]" class="input-field" />
				</div>
				<div class="solo-input" v-for="(input, index) in box1InputsLarge" :key="'box1-large-' + index">
					<label class="input-label">{{ $t(input) }}</label>
					<input v-model.number="inputValues[input]" class="input-field" />
				</div>
				<div class="input-group" v-for="(key, index) in box1Calculated" :key="'box1-' + index">
					<label class="input-label">{{ $t(key) }}</label>
					<!-- Display the calculated result; readonly so user cannot edit -->
					<input :value="calculatedValues[key]" type="text" class="calculated-field" readonly />
				</div>
			</div>

			<!-- Box 2: Calculated Results -->
			<div class="box">
				<div class="solo-input" v-for="(key, index) in box2Calculated" :key="'box2-' + index">
					<label class="input-label">{{ $t(key) }}</label>
					<!-- Display the calculated result; readonly so user cannot edit -->
					<input :value="calculatedValues[key]" type="text" class="calculated-field" readonly />
				</div>
			</div>

			<!-- Box 3: More User Inputs -->
			<div class="box">
				<div class="solo-input" v-for="(input, index) in box3InputsLarge" :key="'box3-large-' + index">
					<label class="input-label">{{ $t(input) }}</label>
					<input v-model.number="inputValues[input]" class="input-field" />
				</div>
				<div class="input-group" v-for="(input, index) in box3Inputs" :key="'box3-' + index">
					<label class="input-label">{{ $t(input) }}</label>
					<input v-model.number="inputValues[input]" class="input-field" />
				</div>
			</div>

			<!-- Box 4: More Calculated Results -->
			<div class="box">
				<div class="solo-input" v-for="(key, index) in box4Calculated" :key="'box4-' + index">
					<label class="input-label">{{ $t(key) }}</label>
					<input :value="calculatedValues[key]" type="text" class="calculated-field" readonly />
				</div>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'RoiCalculator',
	data() {
		return {
			box1Inputs: ['daily_employees_num', 'daily_shift_hours', 'rooms_num', 'floors_num'],
			box1InputsLarge: ['cleaner_entries', 'avg_daily_visits_monthly'],
			box1Calculated: ['cleaners_to_visitors_ratio', 'cleaning_time_in_minutes_per_room'],
			box2Calculated: [
				'avg_daily_cleaners_required',
				'avg_daily_cleaners_to_save',
				'saving_hours_per_day',
				'saving_money_per_day',
				'saving_money_per_month'
			],
			box3Inputs: [
				'break_time_per_hour',
				'transition_time_between_tasks',
				'employer_cost_hour',
				'lobby_cleaning_per_floor',
				'desired_cleaning_time',
				'desired_number_of_entries'
			],
			box3InputsLarge: ['max_number_of_rooms_per_employee'],
			box4Calculated: [
				'buzz_system_cost_per_unit_per_month',
				'total_buzz_system_cost_per_month',
				'return_on_investment_in_months',
				'annual_savings'
			],
			// Object to store user-entered input values
			inputValues: {
				daily_employees_num: 0,
				daily_shift_hours: 0,
				rooms_num: 0,
				floors_num: 0,
				cleaner_entries: 0,
				avg_daily_visits_monthly: 0,
				cleaners_to_visitors_ratio: 0,
				cleaning_time_in_minutes_per_room: 0,
				max_number_of_rooms_per_employee: 0,
				break_time_per_hour: 0,
				transition_time_between_tasks: 0,
				employer_cost_hour: 50,
				lobby_cleaning_per_floor: 0,
				desired_cleaning_time: 0,
				desired_number_of_entries: 0
			}
		}
	},
	computed: {
		// This computed property automatically recalculates whenever an input changes.
		calculatedValues() {
			const inputs = this.inputValues
			// Convert all inputs to numbers (fallback to 0)
			const dailyEmployees = Number(inputs.daily_employees_num) || 0
			const dailyShiftHours = Number(inputs.daily_shift_hours) || 0
			const rooms = Number(inputs.rooms_num) || 0
			const floors = Number(inputs.floors_num) || 0
			const cleanerEntries = Number(inputs.cleaner_entries) || 0
			const avgDailyVisits = Number(inputs.avg_daily_visits_monthly) || 0
			const maxRoomsPerEmployee = Number(inputs.max_number_of_rooms_per_employee) || 0
			const breakTime = Number(inputs.break_time_per_hour) || 0
			const transitionTime = Number(inputs.transition_time_between_tasks) || 0
			const employerCost = Number(inputs.employer_cost_hour) || 50
			const lobbyCleaning = Number(inputs.lobby_cleaning_per_floor) || 0
			const desiredCleaningTime = Number(inputs.desired_cleaning_time) || 0
			const desiredNumberOfEntries = Number(inputs.desired_number_of_entries) || 0

			// === Placeholder algorithm ===
			// Replace these calculations with your own business logic
			const workDayHours = dailyEmployees * dailyShiftHours
			const workDayMinutes = workDayHours * 60

			const transitionTimeBetweenTasksInMinutes = transitionTime * cleanerEntries
			// const transitionTimeBetweenTasksInHours = transitionTimeBetweenTasksInMinutes / 60
			const breakTimeInMinutes = breakTime * (dailyEmployees * dailyShiftHours)
			// const breakTimeInHours = breakTimeInMinutes / 60
			const lobbyCleaningInMinutes = lobbyCleaning * floors
			// const lobbyCleaningInHours = lobbyCleaningInMinutes / 60

			const minutesSum =
				workDayMinutes - (transitionTimeBetweenTasksInMinutes + breakTimeInMinutes + lobbyCleaningInMinutes)
			// const hoursSum = workDayHours - (transitionTimeBetweenTasksInHours + breakTimeInHours + lobbyCleaningInHours)

			const cleaners_to_visitors_ratio = isFinite(avgDailyVisits / cleanerEntries)
				? Math.round(avgDailyVisits / cleanerEntries)
				: 0

			const cleaning_time_in_minutes_per_room = isFinite(minutesSum / cleanerEntries)
				? Math.round(minutesSum / cleanerEntries)
				: 0

			const maxRoomsPerEmployeeInMinutes = isFinite(rooms / maxRoomsPerEmployee + 1)
				? Math.round(rooms / maxRoomsPerEmployee + 1)
				: 0

			const maxRoomsPerEmployeeInHours = isFinite(
				(avgDailyVisits / desiredNumberOfEntries) * (desiredCleaningTime / 60 / dailyShiftHours) + 1
			)
				? Math.round((avgDailyVisits / desiredNumberOfEntries) * (desiredCleaningTime / 60 / dailyShiftHours) + 1)
				: 0

			const avg_daily_cleaners_required =
				Math.round(Math.max(maxRoomsPerEmployeeInHours, maxRoomsPerEmployeeInMinutes)) || 0

			const avg_daily_cleaners_to_save = Math.round(dailyEmployees - avg_daily_cleaners_required) || 0
			const saving_hours_per_day = Math.round(avg_daily_cleaners_to_save * dailyShiftHours) || 0
			const saving_money_per_day = Math.round(saving_hours_per_day * employerCost) || 0
			const saving_money_per_month = Math.round(saving_money_per_day * 24) || 0
			const buzz_system_cost_per_unit_per_month = 100
			const total_buzz_system_cost_per_month = Math.round(buzz_system_cost_per_unit_per_month * rooms) || 0

			return {
				avg_daily_cleaners_required,
				avg_daily_cleaners_to_save,
				saving_hours_per_day,
				saving_money_per_day,
				saving_money_per_month,
				cleaners_to_visitors_ratio,
				cleaning_time_in_minutes_per_room,
				buzz_system_cost_per_unit_per_month,
				total_buzz_system_cost_per_month
				// return_on_investment_in_months,
				// annual_savings
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.roi-calculator {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100vh;
}

.box-container {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-template-rows: repeat(2, 1fr);
	gap: 20px;
	width: 80%;
}

.box {
	border: 2px solid #ccc;
	border-radius: 8px;
	width: 100%;
	height: 300px;
	background-color: #ffffff;
	padding: 20px;
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	overflow-y: auto;
}

.solo-input {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
}

.input-group {
	display: flex;
	flex-direction: column;
	width: calc(50% - 10px);
	align-items: flex-start;
}

.input-label {
	font-size: 14px;
	color: #333;
	margin-bottom: 5px;
}

.input-field {
	width: 50%;
	height: 40px;
	border: 1px solid #ccc;
	border-radius: 4px;
	padding: 5px;
	text-align: center;
}

.calculated-field {
	width: 50%;
	height: 40px;
	border: 1px solid #ccc;
	border-radius: 4px;
	padding: 5px;
	background-color: #f1f1f1;
	text-align: center;
}
</style>
